import Fuse from 'fuse.js';
import { httpClient } from '../../../http-client';
import { ApiUrlBuilder } from '../constants/apiUrl';
import { SiteItemModel } from '../../sites/models/site-item.model';
import { SiteModel } from '../../site/models/site.model';
import { BuildingItemModel } from '../../site/models/buildingItem.model';
import { getCause } from '../../../http-client/interceptors/error.interceptor';
import { addDebugLog } from '../hooks/use-debug-logs.hook';

export interface PaginatedSitesApiResponse {
  items: SiteListItemView[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;
}

export interface SiteListItemView {
  id: string;
  name: string;
  address: string;
  numberOfAnnotations: number;
  numberOfAnnotationsOnArea: number;
  isOffline: boolean;
  isGraphic: boolean;
  isExported: boolean,
  numberOfExportedBuildings: number,
  numberOfBuildings: number
}

export interface BuildingListItemView {
  id: string;
  name: string;
  numberOfAnnotations: number;
  isGraphic: boolean;
  isExported: boolean,
  address: string
}

export interface SiteView {
  id: string;
  name: string;
  address: string;
  hasOverallPlan: boolean;
  buildings: BuildingListItemView[];
  rentalArea: null | string;
  tenantCount: null | string;
  lastRehabilitationDate: null | string;
  floorCount: null | string;
  ownerType: null | string;
  isOffline: boolean;
  numberOfAnnotations: number;
  numberOfAnnotationsOnArea: number;
  isGraphic: boolean;
  isExported: boolean,
  numberOfExportedBuildings: number,
  numberOfBuildings: number
}

const SitesServiceImplementation = {
  getSites: (orga: string, searchTerm?: string, page?: number, pageSize?: number, hasAnnotation?: boolean): Promise<SiteItemModel[]> => {
    const url = ApiUrlBuilder.getSites(orga, page, pageSize);
    addDebugLog('app', `[SitesService] getSites called - org: ${orga}, page: ${page || 1}, pageSize: ${pageSize || 20}, hasAnnotation: ${hasAnnotation || false}, url: ${url}`);
    return httpClient
      .post(url, {
        body: JSON.stringify({
          searchTerm: searchTerm || undefined,
          hasAnnotation: hasAnnotation ?? false
        })
      })
      .then((response) => {
        addDebugLog('app', `[SitesService] Response status: ${response.status}`);
        return response.json();
      })
      .then((response) => {
        // Gérer les réponses paginées (avec items) ou les tableaux directs
        const sitesArray = Array.isArray(response) 
          ? response 
          : (response && Array.isArray(response.items) ? response.items : []);
        
        addDebugLog('app', `[SitesService] Response parsed - ${sitesArray.length} site(s) found`);
        
        return sitesArray.map((site: unknown) => {
          const isSiteListItemView = (siteView: any): siteView is SiteListItemView =>
            'id' in siteView && 'name' in siteView && 'address' in siteView;
          if (isSiteListItemView(site)) {
            return new SiteItemModel(
              site.id,
              site.name,
              site.address,
              site.isOffline,
              site.numberOfAnnotations,
              site.isGraphic,
              site.isExported,
              site.numberOfExportedBuildings,
              site.numberOfBuildings
            );
          } else {
            throw new Error(`response contains: ${JSON.stringify(response)}`, { cause: getCause(500) });
          }
        });
      })
      .catch((error) => {
        // En cas d'erreur réseau, retourner un tableau vide
        // Le service worker devrait gérer le fallback, mais on gère aussi ici pour être sûr
        const errorMsg = error instanceof Error ? error.message : String(error);
        addDebugLog('app', `[SitesService] ERROR fetching sites: ${errorMsg}`);
        console.error('[SitesService] Error fetching sites:', error);
        throw error;
      });
  },
  getSite: (orga: string, siteId: string): Promise<SiteModel> =>
    httpClient
      .get(ApiUrlBuilder.getSite(orga, siteId))
      .then((response) => response.json())
      .then((response) => {
        const isSiteView = (siteView: any): siteView is SiteView =>
          'id' in siteView &&
          'name' in siteView &&
          'address' in siteView &&
          'hasOverallPlan' in siteView &&
          'buildings' in siteView;
        if (isSiteView(response)) {
          return new SiteModel(
            response.id,
            response.name,
            response.address,
            response.hasOverallPlan,
            response.buildings.map((b) => new BuildingItemModel(b.id, b.name, b.numberOfAnnotations, b.isGraphic,b.isExported, b.address)),
            response.rentalArea,
            response.tenantCount,
            response.lastRehabilitationDate,
            response.floorCount,
            response.ownerType,
            response.isOffline,
            response.isGraphic,
            response.isExported,
            response.numberOfExportedBuildings,
            response.numberOfBuildings,
            response.numberOfAnnotations,
            response.numberOfAnnotationsOnArea
          );
        } else {
          throw new Error(`response contains: ${ JSON.stringify(response) }`,
            { cause: getCause(response?.status || 500) });
        }
      }),
  searchSite: (searchTerm: string, siteList: SiteItemModel[]): SiteItemModel[] => {
    const fuseOptions: Fuse.IFuseOptions<SiteItemModel> = {
      ignoreLocation: true,
      threshold: 0.3,
      distance: 50,
      minMatchCharLength: 2,
      keys: ['name', 'address'] as (keyof SiteItemModel)[],
    };
    return searchTerm.length === 0 ? siteList : new Fuse(siteList, fuseOptions).search(searchTerm).map((m) => m.item);
  },
  filterAnnotatedSites: (annotateFilterValue: 'ALL'|'ANNOTATED', siteList: SiteItemModel[]):SiteItemModel[] => {
    const filter = annotateFilterValue === 'ANNOTATED' ? (site: SiteItemModel) => site.numberOfAnnotations > 0 : (site: SiteItemModel)=> true;
    return siteList.filter(filter);
  }
};

export const SitesService: Readonly<typeof SitesServiceImplementation> = SitesServiceImplementation;
